import { useEffect, useCallback, useMemo, useRef } from "react";
import { TouchableOpacity, useWindowDimensions, ActivityIndicator } from "react-native";
import { Image } from "expo-image";
import { View, Text } from "@src/components";
import { useRouter } from "expo-router";
import * as ScreenOrientation from "expo-screen-orientation";
import { useUnistyles } from "react-native-unistyles";
import { useJpegStream } from "@src/features/cameras";
import { StreamingShell, useLandscapeLock } from "@src/features/cameras";
import { useCameraRouteParams } from "@src/hooks/useCameraRouteParams";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { useTenantId } from "@src/features/tenants";
import { Icon } from "@src/components/Icon";
import { useRecordActivity } from "@visioni/a3s-activity/app";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";

export function JpegStreamContent() {
  const { siteId, cameraKey } = useCameraRouteParams({
    requireSiteId: true,
    requireCameraKey: true,
  });
  const tenantId = useTenantId();
  const { theme } = useUnistyles();
  const router = useRouter();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const { track } = useAmplitude();
  const mediaViewPathParams = useMemo(() => {
    if (!siteId || !cameraKey) return undefined;
    return {
      siteId: SiteId.fromString(siteId),
      assetCameraKey: AssetCameraKey.fromString(cameraKey),
    };
  }, [siteId, cameraKey]);
  const { mutate: recordActivity } = useRecordActivity(mediaViewPathParams);
  const hasTrackedStart = useRef(false);

  const { currentFrame, isLoading, error, errorMessage, startStream, stopStream, restartStream, timeRemaining } =
    useJpegStream({
      cameraKey: cameraKey || "",
      tenantId: tenantId || "",
      siteId: siteId || "",
    });

  useEffect(() => {
    track(ANALYTICS_EVENTS.JPEG_STREAM_VIEWED, {
      camera_key: cameraKey,
      tenant_id: tenantId,
      site_id: siteId,
    });
  }, [track, cameraKey, tenantId, siteId]);

  // Lock orientation to landscape immediately (no delay for G1)
  // The hook will automatically reset to portrait on unmount
  useLandscapeLock({ enabled: true, delay: 0 });

  // Stream lifecycle: start on mount, stop on unmount
  useEffect(() => {
    startStream();
    return () => {
      stopStream();
    };
  }, [startStream, stopStream]);

  useEffect(() => {
    if (currentFrame && !isLoading && !hasTrackedStart.current) {
      hasTrackedStart.current = true;
      track(ANALYTICS_EVENTS.JPEG_STREAM_STARTED, {
        camera_key: cameraKey,
        tenant_id: tenantId,
        site_id: siteId,
      });
      if (mediaViewPathParams) {
        recordActivity(
          { kind: "livestream_viewed" },
          {
            onError: (error) => {
              console.warn("Failed to record livestream view activity:", error);
            },
          },
        );
      }
    }
  }, [currentFrame, isLoading, track, cameraKey, tenantId, siteId, mediaViewPathParams, recordActivity]);

  useEffect(() => {
    if (error) {
      const eventName =
        error === "timeout_stream" ? ANALYTICS_EVENTS.JPEG_STREAM_TIMEOUT : ANALYTICS_EVENTS.JPEG_STREAM_ERROR;
      track(eventName, {
        camera_key: cameraKey,
        tenant_id: tenantId,
        site_id: siteId,
        error_type: error,
      });
    }
  }, [error, track, cameraKey, tenantId, siteId]);

  const handleClose = useCallback(async () => {
    // Step 1: Reset orientation to portrait first
    try {
      await ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
    } catch (error) {
      // Orientation lock not supported, continue with navigation
    }

    // Step 2: Wait for orientation change to process, then navigate back
    setTimeout(() => {
      router.back();
    }, 250);
  }, [router]);

  const handleRestart = useCallback(() => {
    hasTrackedStart.current = false;
    restartStream();
  }, [restartStream]);

  const formatTimeRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (error) {
    return (
      <StreamingShell onClose={handleClose}>
        <View flex={1} justifyContent="center" alignItems="center" px="xl">
          <Icon
            name={error === "timeout_stream" ? "clockOutline" : "errorOutline"}
            size={64}
            color={theme.colors.white.default}
          />
          <Text textStyle="lg/semibold" style={{ color: theme.colors.white.default }} mt="lg" textAlign="center">
            {error === "timeout_stream" ? "Stream Session Ended" : "Unable to Stream"}
          </Text>
          <Text textStyle="md/regular" style={{ color: theme.colors.grey[400] }} mt="sm" textAlign="center">
            {errorMessage}
          </Text>

          {error === "timeout_stream" && (
            <TouchableOpacity
              onPress={handleRestart}
              style={{
                marginTop: theme.spacing["3xl"],
                backgroundColor: theme.colors.blue[500],
                paddingHorizontal: theme.spacing["3xl"],
                paddingVertical: theme.spacing.lg,
                borderRadius: theme.spacing.md,
              }}
            >
              <Text textStyle="md/semibold" style={{ color: theme.colors.white.default }}>
                Restart Stream
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </StreamingShell>
    );
  }

  return (
    <StreamingShell onClose={handleClose}>
      <View flex={1} justifyContent="center" alignItems="center">
        {currentFrame ?
          <Image
            source={{ uri: currentFrame }}
            style={{ width: screenWidth, height: screenHeight }}
            contentFit="contain"
            cachePolicy="none"
          />
        : <View
            style={{
              width: screenWidth,
              height: screenHeight,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Icon name="videoOutline" size={64} color={theme.colors.grey[500]} />
          </View>
        }
      </View>

      {isLoading && (
        <View
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
        >
          <ActivityIndicator size="large" color={theme.colors.white.default} />
          <Text textStyle="md/regular" style={{ color: theme.colors.white.default }} mt="lg">
            Starting stream...
          </Text>
        </View>
      )}

      {timeRemaining !== null && timeRemaining > 0 && (
        <View
          style={{
            position: "absolute",
            top: theme.spacing.md,
            right: theme.spacing.xl,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            paddingHorizontal: theme.spacing.md,
            paddingVertical: theme.spacing.xs,
            borderRadius: theme.spacing.sm,
          }}
        >
          <Text textStyle="sm/medium" style={{ color: theme.colors.white.default }}>
            {formatTimeRemaining(timeRemaining)}
          </Text>
        </View>
      )}

      <View
        style={{
          position: "absolute",
          bottom: theme.spacing.lg,
          left: 0,
          right: 0,
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <TouchableOpacity
          onPress={() => {
            console.log("Snapshot - to be implemented");
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            paddingHorizontal: theme.spacing.lg,
            paddingVertical: theme.spacing.md,
            borderRadius: theme.spacing.md,
            gap: theme.spacing.sm,
          }}
        >
          <Icon name="pictureOutline" size={24} color={theme.colors.white.default} />
          <Text textStyle="md/medium" style={{ color: theme.colors.white.default }}>
            Snapshot
          </Text>
        </TouchableOpacity>
      </View>
    </StreamingShell>
  );
}
